/**
 * Reading Room — category metadata for the trivia categories.
 *
 * Each category carries the variant-specific surface data: accent color,
 * backgrounds, motif glyph. The underlying gameplay engine (in
 * `games/trivia/`) is variant-agnostic and only cares about the slug.
 *
 * The slug type re-exports the canonical union from `games/trivia` so the
 * bank and this surface can never drift apart.
 */
import type { TriviaCategorySlug } from '@/games/trivia/categoriesSlug';

export type { TriviaCategorySlug };

export interface CategoryMeta {
  slug: TriviaCategorySlug;
  title: string;
  /** One-line description shown under the title — sets the player's expectation. */
  blurb: string;
  /** CSS variable name — `var(--cobalt)` etc. */
  accentVar: string;
  /** Hex fallback for places that need a value (motion springs etc.). */
  accentSwatch: string;
  accentSwatchDark: string;
  /** Public path served by Vite from `apps/games-web/public/`. */
  backgroundLight: string;
  backgroundDark: string;
  motifKind:
    | 'vinyl'
    | 'cassette'
    | 'atlas'
    | 'film-poster'
    | 'cathode'
    | 'kitchen'
    | 'book'
    | 'paw'
    | 'palette'
    | 'bookstack'
    | 'column'
    | 'flask'
    | 'pennant';
}

export const CATEGORIES: readonly CategoryMeta[] = [
  {
    slug: 'music-60s',
    title: '1960s Music',
    blurb: 'Beatles to Beach Boys',
    accentVar: '--cobalt',
    accentSwatch: '#5A6F8E',
    accentSwatchDark: '#7A92B6',
    backgroundLight: '/generated/trivia-categories/music-60s-light.png',
    backgroundDark: '/generated/trivia-categories/music-60s-dark.png',
    motifKind: 'vinyl',
  },
  {
    slug: 'music-70s',
    title: '1970s Music',
    blurb: 'Disco, rock, and soul',
    accentVar: '--burnt-orange',
    accentSwatch: '#C97A3A',
    accentSwatchDark: '#D88E4F',
    backgroundLight: '/generated/trivia-categories/music-70s-light.png',
    backgroundDark: '/generated/trivia-categories/music-70s-dark.png',
    motifKind: 'cassette',
  },
  {
    slug: 'geography',
    title: 'World Geography',
    blurb: 'Continents, capitals, coastlines',
    accentVar: '--sage',
    accentSwatch: '#7A8F6F',
    accentSwatchDark: '#93A881',
    backgroundLight: '/generated/trivia-categories/geography-light.png',
    backgroundDark: '/generated/trivia-categories/geography-dark.png',
    motifKind: 'atlas',
  },
  {
    slug: 'classic-films',
    title: 'Classic Films',
    blurb: "Hollywood's golden era",
    accentVar: '--terracotta',
    accentSwatch: '#C97B5C',
    accentSwatchDark: '#D88B6A',
    backgroundLight: '/generated/trivia-categories/classic-films-light.png',
    backgroundDark: '/generated/trivia-categories/classic-films-dark.png',
    motifKind: 'film-poster',
  },
  {
    slug: 'tv-classics',
    title: 'TV Classics',
    blurb: 'Living-room favorites',
    accentVar: '--warm-gold',
    accentSwatch: '#B68A3E',
    accentSwatchDark: '#D5A957',
    backgroundLight: '/generated/trivia-categories/tv-classics-light.png',
    backgroundDark: '/generated/trivia-categories/tv-classics-dark.png',
    motifKind: 'cathode',
  },
  {
    slug: 'around-the-house',
    title: 'General Knowledge',
    blurb: 'A little of everything',
    accentVar: '--blush',
    accentSwatch: '#C78A8A',
    accentSwatchDark: '#D6A2A2',
    backgroundLight: '/generated/trivia-categories/around-the-house-light.png',
    backgroundDark: '/generated/trivia-categories/around-the-house-dark.png',
    motifKind: 'book',
  },
  {
    slug: 'animals',
    title: 'Animal Kingdom',
    blurb: 'Creatures great and small',
    accentVar: '--sage',
    accentSwatch: '#7A8F6F',
    accentSwatchDark: '#93A881',
    backgroundLight: '/generated/trivia-categories/animals-light.png',
    backgroundDark: '/generated/trivia-categories/animals-dark.png',
    motifKind: 'paw',
  },
  {
    slug: 'art',
    title: 'Art & Artists',
    blurb: 'Painters, sculptors, masterpieces',
    accentVar: '--blush',
    accentSwatch: '#C78A8A',
    accentSwatchDark: '#D6A2A2',
    backgroundLight: '/generated/trivia-categories/art-light.png',
    backgroundDark: '/generated/trivia-categories/art-dark.png',
    motifKind: 'palette',
  },
  {
    slug: 'books',
    title: 'Books & Authors',
    blurb: 'Novels worth remembering',
    accentVar: '--warm-gold',
    accentSwatch: '#B68A3E',
    accentSwatchDark: '#D5A957',
    backgroundLight: '/generated/trivia-categories/books-light.png',
    backgroundDark: '/generated/trivia-categories/books-dark.png',
    motifKind: 'bookstack',
  },
  {
    slug: 'history',
    title: 'History',
    blurb: 'Moments that shaped the world',
    accentVar: '--burnt-orange',
    accentSwatch: '#C97A3A',
    accentSwatchDark: '#D88E4F',
    backgroundLight: '/generated/trivia-categories/history-light.png',
    backgroundDark: '/generated/trivia-categories/history-dark.png',
    motifKind: 'column',
  },
  {
    slug: 'science',
    title: 'Science & Nature',
    blurb: 'How the world works',
    accentVar: '--cobalt',
    accentSwatch: '#5A6F8E',
    accentSwatchDark: '#7A92B6',
    backgroundLight: '/generated/trivia-categories/science-light.png',
    backgroundDark: '/generated/trivia-categories/science-dark.png',
    motifKind: 'flask',
  },
  {
    slug: 'sports',
    title: 'Sports',
    blurb: 'Games, champions, classic moments',
    accentVar: '--terracotta',
    accentSwatch: '#C97B5C',
    accentSwatchDark: '#D88B6A',
    backgroundLight: '/generated/trivia-categories/sports-light.png',
    backgroundDark: '/generated/trivia-categories/sports-dark.png',
    motifKind: 'pennant',
  },
] as const;

export function getCategory(slug: TriviaCategorySlug): CategoryMeta {
  const found = CATEGORIES.find((c) => c.slug === slug);
  if (!found) {
    throw new Error(`Unknown trivia category: ${slug}`);
  }
  return found;
}

export function nextCategorySlug(current: TriviaCategorySlug): TriviaCategorySlug {
  const i = CATEGORIES.findIndex((c) => c.slug === current);
  return CATEGORIES[(i + 1) % CATEGORIES.length]!.slug;
}
